import { QueryClient } from '@tanstack/query-core';
import type { FetchResponse, RequestParams, RestService } from '../rest-service';
import { DefaultRestService } from '../rest-service';
import { API } from './api';
import { CustomAPI } from './custom-api';

export type Preferences = Record<string, unknown>;

export type SetPreferencesOptions = {
  identifier: string;
  preferences: Preferences;
  requestParams?: Partial<RequestParams>;
};

export type GetPreferencesOptions = {
  identifier: string;
  requestParams?: Partial<RequestParams>;
};

export type RemovePreferenceOptions = {
  identifier: string;
  key: string;
  requestParams?: Partial<RequestParams>;
};

export type ResetPreferencesOptions = {
  identifier: string;
  preferences?: Preferences;
  requestParams?: Partial<RequestParams>;
};

export type UserPreferencesAPIOptions = {
  debounceTime: number;
};

export interface IUserPreferencesAPI {
  setPreferences(options: SetPreferencesOptions): Promise<FetchResponse<void>>;
  getPreferences(options: GetPreferencesOptions): Promise<FetchResponse<Preferences>>;
  removePreference(options: RemovePreferenceOptions): Promise<FetchResponse<void>>;
  resetPreferences(options: ResetPreferencesOptions): Promise<FetchResponse<void>>;
}

/**
 * API for managing user preferences. This is simply a wrapper that uses a
 * CustomAPI to make requests to "UIUserPreference" class in SYDLE ONE.
 *
 * It's not recommended to use this API directly. Instead, use the UserPreferencesAPI
 * that offers cache control and debouncing for setPreferences.
 */
export class UserPreferencesInternalAPI extends API implements IUserPreferencesAPI {
  private readonly preferencesClassId = '65aff2a90bbdf24880fef3ab';
  private customAPI: CustomAPI;

  /**
   * Creates a new UserPreferencesInternalAPI.
   * @param restService The rest service to be used to make requests.
   */
  constructor(restService: RestService = new DefaultRestService()) {
    super(restService);
    this.customAPI = new CustomAPI(this.preferencesClassId, restService);
  }

  /**
   * Sets the preferences for a given identifier. It will not override the
   * existing preferences, but merge them with the ones in the backend.
   * @param options The options for setting preferences.
   * @param options.identifier The identifier for which the preferences are being set.
   * @param options.preferences The preferences to be set.
   * @param options.requestParams Options that can be passed along with the request
   * @returns A promise that resolves when the preferences are set.
   */
  async setPreferences({
    identifier,
    preferences,
    requestParams,
  }: SetPreferencesOptions): Promise<FetchResponse<void>> {
    type SetPreferencesInput = { identifier: string; preferences: Preferences };
    type SetPreferencesOutput = void;

    return await this.customAPI.fetch<SetPreferencesInput, SetPreferencesOutput>(
      'setPreferences',
      {
        identifier,
        preferences,
      },
      { requestParams },
    );
  }

  /**
   * Gets the preferences for a given identifier.
   * @param options The options for getting preferences.
   * @param options.identifier The identifier for which the preferences are being retrieved.
   * @param options.requestParams Options that can be passed along with the request
   * @returns A promise that resolves with the preferences.
   */
  async getPreferences({ identifier, requestParams }: GetPreferencesOptions): Promise<FetchResponse<Preferences>> {
    type GetPreferencesInput = { identifier: string };
    type GetPreferencesOutput = Preferences;

    return await this.customAPI.fetch<GetPreferencesInput, GetPreferencesOutput>(
      'getPreferences',
      {
        identifier,
      },
      { requestParams },
    );
  }

  /**
   * Removes a preference for a given identifier.
   * @param options The options for removing a preference.
   * @param options.identifier The identifier for which the preference is being removed.
   * @param options.key The key of the preference to be removed.
   * @param options.requestParams Options that can be passed along with the request.
   * @returns A promise that resolves when the preference is removed.
   */
  async removePreference({ identifier, key, requestParams }: RemovePreferenceOptions): Promise<FetchResponse<void>> {
    type RemovePreferenceInput = { identifier: string; key: string };
    type RemovePreferenceOutput = void;

    return await this.customAPI.fetch<RemovePreferenceInput, RemovePreferenceOutput>(
      'removePreference',
      {
        identifier,
        key,
      },
      { requestParams },
    );
  }

  /**
   * Resets the preferences for a given identifier. If no preferences are provided,
   * it will empty the preferences for the given identifier. If preferences are provided,
   * it will reset the preferences to the provided ones.
   * @param options The options for resetting preferences.
   * @param options.identifier The identifier for which the preferences are being reset.
   * @param options.preferences The preferences to be reset. If not provided, all preferences are reset.
   * @param options.requestParams Options that can be passed along with the request
   * @returns A promise that resolves when the preferences are reset.
   */
  async resetPreferences({
    identifier,
    preferences,
    requestParams,
  }: ResetPreferencesOptions): Promise<FetchResponse<void>> {
    type ResetPreferencesInput = { identifier: string; preferences?: Preferences };
    type ResetPreferencesOutput = void;

    return await this.customAPI.fetch<ResetPreferencesInput, ResetPreferencesOutput>(
      'resetPreferences',
      {
        identifier,
        preferences,
      },
      { requestParams },
    );
  }
}

/**
 * Client that offers cache control and debouncing for UserPreferencesInternalAPI.
 *
 * When using the UserPreferencesInternalAPI directly, some challenges arise, such as:
 * - How to make a get request not wait for a set request to finish?
 * - How to debounce set requests and avoid making multiple requests for the same identifier?
 * - How to accumulate set requests and make a single request with all the preferences that were set?
 *
 * All these features are implemented in the UserPreferencesAPI. It uses a
 * UserPreferencesInternalAPI to make requests to the backend, but it also offers cache
 * control for all requests and debouncing for setPreferences.
 */
export class UserPreferencesAPI {
  private preferencesAccumulator: UserPreferencesAccumulator;
  private debounceTimeout: Map<string, NodeJS.Timeout>;
  private queryClient: QueryClient;
  private preferencesKeys = {
    get: (identifier: string) => ['getPreferences', identifier] as const,
  };

  /**
   * Creates a new UserPreferencesAPI.
   * @param preferencesAPI The IUserPreferencesAPI to be used to make requests.
   * @param [options] The options for the UserPreferencesAPI.
   * @param options.debounceTime The time in milliseconds to debounce setPreferences requests.
   */
  constructor(
    private preferencesAPI: IUserPreferencesAPI,
    private options: UserPreferencesAPIOptions = { debounceTime: 500 },
  ) {
    this.preferencesAccumulator = new UserPreferencesAccumulator();
    this.debounceTimeout = new Map();
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    });
  }

  /**
   * Sets the preferences for a given identifier.
   * It also debounces the requests and accumulates the preferences being set to avoid multiple requests.
   * @param options The options for setting preferences.
   * @param options.identifier The identifier for which the preferences are being set.
   * @param options.preferences The preferences to be set.
   * @param options.requestParams Options that can be passed along with the request.
   */
  setPreferences({ identifier, preferences, requestParams }: SetPreferencesOptions) {
    this.preferencesAccumulator.add(identifier, preferences);

    this.queryClient.cancelQueries({ queryKey: this.preferencesKeys.get(identifier) });
    this.queryClient.setQueryData<Preferences>(this.preferencesKeys.get(identifier), (cachedPreferences) => {
      if (!cachedPreferences) {
        return undefined;
      }

      return { ...cachedPreferences, ...preferences };
    });

    this.debounce(identifier, async () => {
      const preferencesInAccumulator = this.preferencesAccumulator.get(identifier);

      // If the user removed the preferences, we don't need to make a request.
      if (Object.keys(preferencesInAccumulator).length === 0) {
        return;
      }

      this.preferencesAccumulator.delete(identifier);
      await this.preferencesAPI.setPreferences({ identifier, preferences: preferencesInAccumulator, requestParams });
      this.queryClient.invalidateQueries({ queryKey: this.preferencesKeys.get(identifier), refetchType: 'all' });
    });
  }

  /**
   * Gets the preferences for a given identifier.
   * If the preferences are in the cache, it will return them immediately.
   * @param options The options for getting preferences.
   * @param options.identifier The identifier for which the preferences are being retrieved.
   * @param options.requestParams Options that can be passed along with the request.
   * @returns A promise that resolves with the preferences.
   */
  async getPreferences({ identifier, requestParams }: GetPreferencesOptions): Promise<Preferences> {
    return await this.queryClient.ensureQueryData<Preferences>({
      queryKey: this.preferencesKeys.get(identifier),
      queryFn: async () => {
        const response = await this.preferencesAPI.getPreferences({ identifier, requestParams });
        if (response.error) {
          return {};
        }
        return response.data;
      },
    });
  }

  /**
   * Removes a preference for a given identifier and key.
   * @param options The options for removing a preference.
   * @param options.identifier The identifier for which the preference is being removed.
   * @param options.key The key of the preference to be removed.
   * @param options.requestParams Options that can be passed along with the request.
   */
  removePreference({ identifier, key, requestParams }: RemovePreferenceOptions) {
    if (this.preferencesAccumulator.has(identifier)) {
      delete this.preferencesAccumulator.get(identifier)[key];
    }

    this.queryClient.cancelQueries({ queryKey: this.preferencesKeys.get(identifier) });
    this.queryClient.setQueryData<Preferences>(this.preferencesKeys.get(identifier), (cachedPreferences) => {
      if (!cachedPreferences) {
        return undefined;
      }

      const newCachedPreferences = { ...cachedPreferences };
      delete newCachedPreferences[key];

      return newCachedPreferences;
    });

    this.preferencesAPI.removePreference({ identifier, key, requestParams }).finally(() => {
      this.queryClient.invalidateQueries({ queryKey: this.preferencesKeys.get(identifier), refetchType: 'all' });
    });
  }

  /**
   * Resets the preferences for a given identifier. If no preferences are provided,
   * it will empty the preferences for the given identifier. If preferences are provided,
   * it will reset the preferences to the provided ones.
   * @param options The options for resetting preferences.
   * @param options.identifier The identifier for which the preferences are being reset.
   * @param [options.preferences] The preferences to be reset. If not provided, all preferences are reset.
   * @param options.requestParams Options that can be passed along with the request.
   */
  resetPreferences({ identifier, preferences, requestParams }: ResetPreferencesOptions) {
    this.queryClient.cancelQueries({ queryKey: this.preferencesKeys.get(identifier) });
    this.queryClient.setQueryData<Preferences>(this.preferencesKeys.get(identifier), preferences);

    this.preferencesAccumulator.delete(identifier);
    this.preferencesAPI.resetPreferences({ identifier, preferences, requestParams }).finally(() => {
      this.queryClient.invalidateQueries({ queryKey: this.preferencesKeys.get(identifier), refetchType: 'all' });
    });
  }

  private debounce(identifier: string, callback: () => void): void {
    if (this.debounceTimeout.has(identifier)) {
      clearTimeout(this.debounceTimeout.get(identifier));
    }

    this.debounceTimeout.set(identifier, setTimeout(callback, this.options.debounceTime));
  }
}

/**
 * Accumulates the preferences being set to avoid multiple backend requests.
 * It's used by the UserPreferencesAPI to accumulate the preferences being
 * set until the debouncing time is over.
 *
 * This class abstracts the logic of accumulating preferences and merging them,
 * so the UserPreferencesAPI doesn't need to worry about it.
 */
class UserPreferencesAccumulator {
  private accumulator: Map<string, Preferences> = new Map();

  add(identifier: string, preferences: Preferences): void {
    const preferencesInAccumulator = this.accumulator.get(identifier);

    if (!preferencesInAccumulator) {
      this.accumulator.set(identifier, preferences);
      return;
    }

    this.accumulator.set(identifier, { ...preferencesInAccumulator, ...preferences });
  }

  has(identifier: string): boolean {
    const preferences = this.accumulator.get(identifier);

    return Boolean(preferences);
  }

  get(identifier: string): Preferences {
    const preferences = this.accumulator.get(identifier);

    if (!preferences) {
      throw new Error(`Preferences for identifier ${identifier} not found in accumulator.`);
    }

    return preferences;
  }

  delete(identifier: string): void {
    this.accumulator.delete(identifier);
  }

  clear() {
    this.accumulator.clear();
  }
}
